import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaSearch, FaMoneyBillWave, FaUniversity, FaMoneyCheck, FaArrowLeft } from 'react-icons/fa';
import moment from 'moment';
import { ENDPOINT } from '../api/auth';
import './OrderHistory.css';

const banks = [
  { value: 'CBE', label: 'CBE' },
  { value: 'Awash', label: 'Awash' },
  { value: 'Dashen', label: 'Dashen' },
  { value: 'Abyssinia', label: 'Abyssinia' },
  { value: 'Birhan', label: 'Birhan' },
  { value: 'Telebirr', label: 'Telebirr' },
];

const OrderHistory = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: 'cash',
    bankName: 'CBE',
  });
  const [processing, setProcessing] = useState(false);

  // Fetch orders based on status filter
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          ENDPOINT,
          {},
          {
            params: { 
              action: 'get_product_orders',
              status: statusFilter === 'all' ? '' : statusFilter 
            },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const ordersData = response.data.orders || [];
        setOrders(ordersData);
        setFilteredOrders(ordersData);
      } catch (error) {
        console.error('Order fetch error:', error);
        toast.error('Failed to fetch orders');
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrders();
  }, [statusFilter]);

  // Handle search
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredOrders(orders);
    } else {
      const filtered = orders.filter(
        order => 
          order.product_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          order.id.toString().includes(searchQuery) ||
          order.status?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredOrders(filtered);
    }
  }, [searchQuery, orders]);

  // Handle order receive
  const handleReceive = async (orderId) => {
    if (!window.confirm('Are you sure you want to mark this order as received?')) return;
    
    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      
      // Send the order_id in the request body instead of params
      await axios.post(
        `${ENDPOINT}?action=receiveOrder`,
        { order_id: orderId },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Refresh orders
      const response = await axios.post(
        ENDPOINT,
        {},
        {
          params: { 
            action: 'get_product_orders',
            status: statusFilter === 'all' ? '' : statusFilter 
          },
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        }
      );
      
      setOrders(response.data.orders || []);
      setFilteredOrders(response.data.orders || []);
      toast.success('Order marked as received');
    } catch (error) {
      console.error('Receive error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update order status';
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  // Handle payment submission
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (parseFloat(paymentData.amount) > parseFloat(selectedOrder.unpaid_amount)) {
      toast.error('Amount exceeds unpaid balance');
      return;
    }

    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      const payload = {
        action: 'payOrderCredit',
        order_id: selectedOrder.id,
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.paymentMethod,
        bank_name: paymentData.bankName
      };

      await axios.post(ENDPOINT, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Payment recorded successfully');
      
      // Refresh orders and reset payment form
      const response = await axios.post(
        ENDPOINT,
        {},
        {
          params: { 
            action: 'get_product_orders',
            status: statusFilter === 'all' ? '' : statusFilter 
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setOrders(response.data.orders || []);
      setFilteredOrders(response.data.orders || []);
      setSelectedOrder(null);
      setPaymentData({
        amount: '',
        paymentMethod: 'cash',
        bankName: 'CBE',
      });
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Render order list
  const renderOrderList = () => (
    <div className="order-history-container">
      <div className="order-history-header">
        <h2>Order History</h2>
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-buttons">
          <button
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${statusFilter === 'ordered' ? 'active' : ''}`}
            onClick={() => setStatusFilter('ordered')}
          >
            Ordered
          </button>
          <button
            className={`filter-btn ${statusFilter === 'received' ? 'active' : ''}`}
            onClick={() => setStatusFilter('received')}
          >
            Received
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="empty-state">
          <p>No orders found</p>
        </div>
      ) : (
        <div className="orders-list">
          {filteredOrders.map((order) => (
            <div key={order.id} className="order-card">
              <div className="order-card-header">
                <h3 className="order-id">Order #{order.id}</h3>
                <span className={`status-badge ${order.status}`}>
                  {order.status}
                </span>
              </div>
              
              <div className="order-details">
                <h4 className="product-name">{order.product_name}</h4>
                
                <div className="detail-row">
                  <span className="detail-label">Quantity:</span>
                  <span className="detail-value">{order.quantity}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Order Price:</span>
                  <span className="detail-value">{formatCurrency(order.ordered_price)} ብር</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Selling Price:</span>
                  <span className="detail-value">{formatCurrency(order.selling_price)} ብር</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Payment Method:</span>
                  <span className="detail-value">{order.payment_method || 'N/A'}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Paid:</span>
                  <span className="detail-value">{formatCurrency(order.paid_amount)} ብር</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Unpaid:</span>
                  <span className="detail-value">{formatCurrency(order.unpaid_amount)} ብር</span>
                </div>
                
                <div className="order-dates">
                  <p className="date-text">
                    Ordered: {moment(order.created_at).format('MMM D, YYYY h:mm A')}
                  </p>
                  {order.received_at && (
                    <p className="date-text">
                      Received: {moment(order.received_at).format('MMM D, YYYY h:mm A')}
                    </p>
                  )}
                </div>
                
                <div className="action-buttons">
                  {order.status === 'ordered' && (
                    <button
                      className="btn receive-btn"
                      onClick={() => handleReceive(order.id)}
                      disabled={processing}
                    >
                      {processing ? 'Processing...' : 'Mark as Received'}
                    </button>
                  )}
                  
                  {order.unpaid_amount > 0 && order.status === 'received' && (
                    <button
                      className="btn pay-btn"
                      onClick={() => setSelectedOrder(order)}
                    >
                      Pay Credit
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render payment form
  const renderPaymentForm = () => (
    <div className="payment-container">
      <div className="payment-card">
        <div className="payment-header">
          <button 
            className="back-button"
            onClick={() => setSelectedOrder(null)}
          >
            <FaArrowLeft />
          </button>
          <h2>Pay Credit for Order #{selectedOrder.id}</h2>
        </div>
        
        <div className="payment-details">
          <h3 className="product-name">{selectedOrder.product_name}</h3>
          
          <div className="detail-row">
            <span className="detail-label">Unpaid Amount:</span>
            <span className="detail-value">{formatCurrency(selectedOrder.unpaid_amount)} ብር</span>
          </div>
          
          <form onSubmit={handlePaymentSubmit}>
            <div className="form-group">
              <label>Payment Amount</label>
              <input
                type="number"
                className="form-input"
                placeholder="Enter amount"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                step="0.01"
                min="0.01"
                max={selectedOrder.unpaid_amount}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Payment Method</label>
              <div className="payment-methods">
                <button
                  type="button"
                  className={`payment-method-btn ${paymentData.paymentMethod === 'cash' ? 'active' : ''}`}
                  onClick={() => setPaymentData({...paymentData, paymentMethod: 'cash'})}
                >
                  <FaMoneyBillWave className="method-icon" />
                  <span>Cash</span>
                </button>
                
                <button
                  type="button"
                  className={`payment-method-btn ${paymentData.paymentMethod === 'bank' ? 'active' : ''}`}
                  onClick={() => setPaymentData({...paymentData, paymentMethod: 'bank'})}
                >
                  <FaUniversity className="method-icon" />
                  <span>Bank</span>
                </button>
              </div>
            </div>
            
            {paymentData.paymentMethod === 'bank' && (
              <div className="form-group">
                <label>Bank Name</label>
                <select
                  className="form-input"
                  value={paymentData.bankName}
                  onChange={(e) => setPaymentData({...paymentData, bankName: e.target.value})}
                  required
                >
                  {banks.map((bank) => (
                    <option key={bank.value} value={bank.value}>
                      {bank.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => setSelectedOrder(null)}
                disabled={processing}
              >
                Cancel
              </button>
              
              <button
                type="submit"
                className="btn btn-submit"
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Record Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return selectedOrder ? renderPaymentForm() : renderOrderList();
};

export default OrderHistory;
